import { useState, useCallback } from "react";
import { View } from "react-native";
import { Text } from "@louise/ui/Text/Text";
import { Check, Pencil, RotateCcw } from "lucide-react-native";
import * as DocumentPicker from "expo-document-picker";
import {
  DocumentFileDropZone,
  type SelectedFile,
} from "@src/packages/document-vault/DocumentFileDropZone";
import { useTranslations } from "@src/packages/i18n";
import { ActionBanner, IconButton } from "@louise/moon-ui";
import { semanticColors } from "@louise/theme/tokens";
import { ThinkingOrbStatus } from "@src/components/ui/ThinkingOrbStatus";
import {
  useDocumentSourceTransfer,
  type DocumentSourceFile,
} from "@src/packages/documents/ingestion";

type Props = {
  /** Current document ID (or null if nothing uploaded yet). */
  value: string | null;
  /** Called with the document ID and upload ID after a successful upload. */
  onChange: (documentId: string | null, uploadId: string | null) => void;
  /** Optional call-to-action rendered on the drop zone. Falls back to the
   * canonical "Choose file" / drag-and-drop copy when omitted. */
  label?: string;
  /** Tags applied to the created document so it groups properly in the vault. */
  tags?: string[];
  /** Optional hex / theme colors so the busy/uploaded chip matches the card. */
  bg?: string;
  border?: string;
  text?: string;
};

/**
 * Inline upload field for a treatment step. Uses the shared drag-and-drop
 * document surface (`DocumentFileDropZone`) — the same one the AMH/AFC source
 * sheets use — then uploads the file, creates a Document record, and returns
 * both resource IDs via `onChange` so the parent step can persist the correct
 * relationship on save.
 */
export function TreatmentUploadField({
  value,
  onChange,
  label,
  tags,
  bg = semanticColors.surface.muted,
  border = semanticColors.border.strong,
  text = semanticColors.text.primary,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<DocumentSourceFile | null>(
    null,
  );
  const t = useTranslations();

  const transfer = useDocumentSourceTransfer();
  const busy = transfer.state === "uploading" || transfer.state === "filing";

  const uploadAsset = useCallback(
    async (asset: DocumentSourceFile) => {
      setError(null);
      setPendingFile(asset);
      try {
        const result = await transfer.transfer(asset, {
          name: asset.name || "Untitled",
          tags: tags ?? ["medical_records"],
        });
        setFilename(asset.name || "Untitled");
        onChange(result.documentId, result.uploadId);
      } catch (e) {
        console.warn("Treatment upload failed:", e);
        setError(e instanceof Error ? e.message : "Upload failed");
      }
    },
    [onChange, tags, transfer],
  );

  const retryUpload = useCallback(async () => {
    if (!pendingFile) return;
    setError(null);
    try {
      const sourceUploadId = transfer.uploadId;
      let documentId: string;
      let uploadId: string;
      if (sourceUploadId) {
        documentId = await transfer.retryFile();
        uploadId = sourceUploadId;
      } else {
        const result = await transfer.transfer(pendingFile, {
          name: pendingFile.name || "Untitled",
          tags: tags ?? ["medical_records"],
        });
        documentId = result.documentId;
        uploadId = result.uploadId;
      }
      setFilename(pendingFile.name || "Untitled");
      onChange(documentId, uploadId);
    } catch (retryError) {
      console.warn("Treatment upload retry failed:", retryError);
      setError(
        retryError instanceof Error ? retryError.message : "Upload failed",
      );
    }
  }, [onChange, pendingFile, tags, transfer]);

  const handlePick = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets?.[0]) return;
      const asset = result.assets[0];
      await uploadAsset({
        uri: asset.uri,
        name: asset.name,
        mimeType: asset.mimeType,
      });
    } catch {
      // Picker cancellation / read errors surface via the error state below.
    }
  }, [uploadAsset]);

  const handleFilesSelected = useCallback(
    (files: SelectedFile[]) => {
      const file = files[0];
      if (!file) return;
      void uploadAsset({
        uri: file.uri,
        name: file.name,
        mimeType: file.mimeType,
      });
    },
    [uploadAsset],
  );

  // The raw error is developer text ("Failed to fetch"), so the notice shows
  // the translated failure and the raw one goes to the log.
  const errorBanner = error ? (
    <ActionBanner
      variant="important"
      message={t("documents.upload.failure.upload_failed.title")}
      actionLabel={pendingFile ? t("documents.upload.retry_source") : undefined}
      onAction={pendingFile ? () => void retryUpload() : undefined}
      action="retry_upload"
      actionIcon={RotateCcw}
    />
  ) : null;

  if (busy) {
    return (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          paddingVertical: 10,
          paddingHorizontal: 12,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: border,
          backgroundColor: bg,
        }}
      >
        <ThinkingOrbStatus
          state={transfer.state === "filing" ? "shaping" : "composing"}
          label={
            transfer.state === "filing"
              ? t("documents.upload.source_filing")
              : t("documents.upload.source_uploading")
          }
          size={20}
        />
      </View>
    );
  }

  if (value) {
    return (
      <View style={{ gap: 6 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            paddingVertical: 10,
            paddingHorizontal: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: border,
            backgroundColor: bg,
          }}
        >
          <Check size={16} color={text} strokeWidth={1.8} />
          <Text
            style={{ fontSize: 13, color: text, flex: 1 }}
            numberOfLines={1}
          >
            {filename ?? t("documents.upload.source_saved")}
          </Text>
          <IconButton
            accessibilityLabel={t("documents.upload.replace_file")}
            action="replace_upload"
            onPress={() => void handlePick()}
            hitSlop={8}
            size={30}
            surface="frosted"
          >
            <Pencil size={14} color={text} strokeWidth={1.8} />
          </IconButton>
        </View>
        {errorBanner}
      </View>
    );
  }

  return (
    <View>
      <DocumentFileDropZone
        compact
        onPick={handlePick}
        onFilesSelected={handleFilesSelected}
        primaryLabel={label}
      />
      {errorBanner ? <View style={{ marginTop: 6 }}>{errorBanner}</View> : null}
    </View>
  );
}
